import { useUpflowyStoreRefs } from '@upflowy/firestore';
import { logger } from '@upflowy/logger';
import {
  CONSUMER_APP_SOURCE,
  ConsumerEventType,
  CreateEventType,
  ORIGIN_STATE_ID,
  Targets,
  WebhookDoc,
  xstate,
} from '@upflowy/types';
import { useMachine } from '@xstate/react';
import React, { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { mergeMap } from 'rxjs/operators';
import { useDeepCompareMemo } from 'use-deep-compare';
import { v4 as uuidv4 } from 'uuid';
import { assign, createMachine, State, StateSchema, StateValue } from 'xstate';

import { useApp } from './app-provider';
import { useEventListener } from './event-listener-provider';
import { startAbTest } from './state-machine-actions';

import {
  EVENT_TYPE_BACK,
  EVENT_TYPE_DISMISS,
  EVENT_TYPE_FLOW_COMPLETE,
  EVENT_TYPE_FLOW_READY,
  EVENT_TYPE_FLOW_TRIGGER,
  EVENT_TYPE_REDIRECT,
  EVENT_TYPE_STATE_ENTER,
  EVENT_TYPE_SUBMIT,
  interpolateMustachifiedHTML,
} from '../helpers';
import { addActionsToEvents } from '../helpers/context-events';
import { dataParseFlattenedJSON } from '../helpers/data-parse-flattened-json';
import { fetchDocs } from '../helpers/fetch-documents';
import findApplicableWebhook from '../helpers/findApplicableWebhook';
import { expressionGuard, findXstateConditionStrings } from '../helpers/machine-options-guards';
import stepIsSynchronous from '../helpers/stepIsSynchronous';
import { addHttps } from '../helpers/utils';
import { sendEvent } from '../services/callable-functions';
import { IContext, StateEvent, XStateMachineConfig } from '../types';

export const StateMachineContext = React.createContext<{
  state?: State<IContext, StateEvent>;
  hasStepOnBackBtn: boolean;
  busy: boolean;
  errorMessage: string;
  restoreSubmits: boolean;
}>({ busy: true, hasStepOnBackBtn: false, errorMessage: '', restoreSubmits: false });

type Props = {
  machineConfig: XStateMachineConfig;
  initialState: string | undefined;
  initialContext: Record<string, unknown>;
  flowId: string;
  flowVersionId: string;
  userIdToken: string;
};

type CreateEventResponse = {
  data: {
    success: boolean;
    message: string;
    error: Error | null;
    trace?: {
      respondedAt: number;
      responseStatus: number;
      response?: Record<string, unknown> | string;
      errors?: Record<string, unknown>;
    };
  };
};

export const useStateMachine = () => useContext(StateMachineContext);

export function StateMachineProvider({
  machineConfig,
  initialState,
  initialContext,
  flowId,
  flowVersionId,
  children,
  userIdToken,
}: PropsWithChildren<Props>) {
  const { isStandaloneApp, implementation, containerId } = useApp();
  const machine = useMemo(
    () =>
      createMachine(
        addActionsToEvents(
          {
            ...machineConfig,
            ...(initialState && { initial: initialState }),
          },
          {
            [EVENT_TYPE_SUBMIT]: 'assignEventDataToContext',
            [EVENT_TYPE_FLOW_TRIGGER]: 'strictDataToContext',
            [EVENT_TYPE_DISMISS]: 'clearContext',
          },
        ),
        {
          actions: {
            strictDataToContext: assign((_ctx, event) => ({
              ...initialContext,
              ...event.data,
            })),
            assignEventDataToContext: assign((ctx, event) => ({
              ...ctx,
              ...event.data,
            })),
            clearContext: assign((_ctx) => ({
              ...initialContext,
            })),
            startAbTest,
          },
          guards: findXstateConditionStrings(machineConfig).reduce(
            (a, cond) => ({ ...a, [cond]: expressionGuard }),
            {},
          ),
        },
      ),
    [initialContext, initialState, machineConfig],
  );
  const [errors, setErrors] = useState<Record<string, unknown>>();
  const [errorMessage, setErrorMessage] = useState('' as string);
  const [validFlow, setValidFlow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [restoreSubmits, restoreSubmitState] = useState(false);
  const [promises, setPromises] = useState([] as Promise<CreateEventResponse>[]);
  const [hasStepOnBackBtn, setHasStepOnBackBtn] = useState(false);
  const { origin, eventObserver$, sendParent } = useEventListener();
  const { flows } = useUpflowyStoreRefs();
  const [state, sendToMachine] = useMachine(machine, {
    context: initialContext,
    devTools: process.env.NODE_ENV === 'development',
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const flowBaseEvent = useDeepCompareMemo(
    () => ({
      flowId,
      flowVersionId,
      meta: null,
      securedFormValues: null,
      idToken: userIdToken,
      nextState: null,
      simulated: false,
      origin,
      implementation,
      context: initialContext,
    }),
    [implementation, flowId, flowVersionId, initialContext, initialState, origin, userIdToken],
  );

  const chainEvent = React.useCallback(
    (event: Promise<CreateEventResponse>) => {
      setPromises([...promises, event]);
    },
    [promises],
  );

  useEffect(() => {
    sendParent({
      state: initialState ?? ORIGIN_STATE_ID,
      type: EVENT_TYPE_FLOW_READY,
      flowId,
      data: {
        containerId: containerId || '',
        source: CONSUMER_APP_SOURCE,
      },
    });
    sendEvent({
      ...flowBaseEvent,
      sendAt: new Date().getTime(),
      target: 'flow',
      stepId: null,
      triggerId: null,
      state: initialState ?? ORIGIN_STATE_ID,
      type: EVENT_TYPE_FLOW_READY,
    });
  }, [flowId, flowBaseEvent, initialState, sendParent, containerId]);

  const getType = (meta: Record<string, string> | null) =>
    meta?.type === 'end' ? meta.action?.toUpperCase() : EVENT_TYPE_STATE_ENTER;

  useEffect(() => {
    if (!isStandaloneApp) return;
    const currentState = stateRef.current;
    const currentMetaKeyName = Object.keys(currentState.meta)[0];
    if (currentMetaKeyName === ORIGIN_STATE_ID) {
      const stateOnFlowTriggerEvent = machine.transition(currentState as unknown as StateValue, {
        type: EVENT_TYPE_FLOW_TRIGGER,
      });
      if (stateOnFlowTriggerEvent.transitions[0]?.source?.meta?.type === 'end') {
        setValidFlow(false);
        return;
      }
      setValidFlow(true);
      sendToMachine({
        type: EVENT_TYPE_FLOW_TRIGGER,
        data: {
          sessionId: uuidv4(),
        },
      });
    }
  }, [initialState, isStandaloneApp, machine, sendToMachine, state]);

  useEffect(() => {
    state.context.stateEnteredDate = new Date();
  }, [state]);

  useEffect(() => {
    const currentState = stateRef.current;
    const stateOnBackEvent = machine.transition(currentState as unknown as StateValue, {
      type: EVENT_TYPE_BACK,
    });
    setHasStepOnBackBtn(stateOnBackEvent.value !== ORIGIN_STATE_ID);
  }, [machine, state]);

  useEffect(() => {
    const subscription = eventObserver$
      .pipe(
        mergeMap(async (event): Promise<[xstate.StateEvent, WebhookDoc[]]> => {
          const flowRef = flows().id(flowId).versions().id(flowVersionId);
          const querySnapshot = await fetchDocs(flowRef.webhooks().ref);
          const hooks = querySnapshot.docs.map((d) => d.data());
          return [event, hooks];
        }),
        mergeMap(async ([event, hooks]) => {
          try {
            setErrors(undefined);
            const currentState = stateRef.current;

            const currentMetaKeyName = Object.keys(currentState.meta)[0];
            const { stepId } = currentState.meta[currentMetaKeyName];
            const hook = findApplicableWebhook(stepId, hooks, event);
            const isAsync = hook ? !stepIsSynchronous(hooks, event) : true;
            const currentStateContext = currentState.context;

            let target: Targets = 'step';
            // TODO: REFACTOR TO AVOID MUTATING THE CONTEXT
            if (event.type === EVENT_TYPE_FLOW_TRIGGER) {
              target = 'trigger';
              currentStateContext.triggerId = event.meta?.triggerId;
              // Generate sessionId only for triggered flows
              if (event.meta?.triggerId !== null) currentStateContext.sessionId = uuidv4();
              currentStateContext[`meta.triggerType`] = event.meta?.triggerId;
            }

            const eventData: CreateEventType = {
              sendAt: new Date().getTime(),
              flowId,
              flowVersionId,
              target,
              stepId: stepId ?? null,
              triggerId: (currentStateContext?.triggerId as string) ?? null,
              type: event.type,
              meta: event.meta ?? null,
              context: currentStateContext,
              state: currentState.value as string,
              securedFormValues: event.secure ?? null,
              idToken: userIdToken,
              nextState: null,
              simulated: false,
              origin,
              implementation,
            };

            const parentEventData = {
              flowId,
              type: event.type as ConsumerEventType,
              redirectUrl: event.redirectUrl ? addHttps(event.redirectUrl) : undefined,
              step: stepId && {
                id: stepId,
              },
              state: `${state.value}`,
              data: { source: CONSUMER_APP_SOURCE, containerId: containerId ?? '', ...event.data },
            } as Parameters<typeof sendParent>[0];

            const context = { ...currentStateContext, ...event.data };
            const asyncResult = sendEvent({ ...eventData, context });
            if (!isAsync) {
              setBusy(true);
              restoreSubmitState(false);
              try {
                const eventResult = await asyncResult;

                const webHookErrorMessage =
                  eventResult.data.trace?.errors.message ||
                  'There was an issue proceeding to the next step.';
                if (!eventResult.data.success) {
                  setErrorMessage(`${webHookErrorMessage}`);
                  setErrors(eventResult.data.trace?.errors);
                  restoreSubmitState(true);
                  return asyncResult;
                }

                const JSONData = JSON.parse(eventResult.data.trace?.response || '""');
                const isNotFormattedCorrectly =
                  !JSONData.data.flattenedJSON || !JSONData.data.softFlattened;

                if (isNotFormattedCorrectly) {
                  setErrorMessage(`${webHookErrorMessage}`);
                }
                // console.log("JSONDATA BEFORE", JSONData.data.flattenedJSON);
                JSONData.data.flattenedJSON = JSON.stringify(
                  dataParseFlattenedJSON(JSONData.data.flattenedJSON),
                );

                if (!JSONData.data.softFlattened) {
                  JSONData.data.flattenedJSON = '""';
                  JSONData.data.softFlattened = '""';
                }

                if (eventResult.data.trace?.response) {
                  sendToMachine({
                    ...event,
                    data: {
                      ...event.data,
                      responseStatus: eventResult.data.trace.responseStatus,
                      data: JSON.parse(JSONData.data.softFlattened),
                      ...JSON.parse(JSONData.data.flattenedJSON),
                    },
                  });
                  const { afterSubmit } = state.meta[currentMetaKeyName];
                  const statePaths = state.toStrings();
                  const namespace = statePaths[statePaths.length - 1];
                  const responseData = JSON.parse(eventResult.data.trace.response);
                  if (
                    afterSubmit &&
                    afterSubmit === EVENT_TYPE_REDIRECT &&
                    responseData[`${namespace}.user.redirectPath`]
                  ) {
                    sendParent({
                      ...parentEventData,
                      type: EVENT_TYPE_REDIRECT,
                      redirectUrl: addHttps(
                        responseData[`${namespace}.user.redirectPath`] as string,
                      ),
                    });
                  } else {
                    sendParent(parentEventData);
                  }
                }
              } catch (error) {
                logger.log('Unable to send event', error);
              } finally {
                restoreSubmitState(false);
                setBusy(false);
              }
            } else {
              setBusy(event.type === EVENT_TYPE_SUBMIT);
              chainEvent(asyncResult);
              sendToMachine({
                ...event,
                data: context,
              });
              sendParent(parentEventData);
            }
            return await asyncResult;
          } catch (error) {
            // Catching error here as it seems there is a bug in rxjs before release 7 that
            // does not catch errors from promises
            logger.logException(error);
            return false;
          }
        }),
      )
      .subscribe({
        error: (error) => logger.warn(`Error while sending event ${error.message}`),
      });
    return () => {
      subscription.unsubscribe();
      setBusy(false);
    };
  }, [
    eventObserver$,
    flowId,
    flowVersionId,
    origin,
    sendParent,
    sendToMachine,
    state,
    userIdToken,
    implementation,
    flows,
    chainEvent,
    containerId,
  ]);

  useEffect(() => {
    const currentState = stateRef.current;
    const currentMetaKeyName = Object.keys(currentState.meta)[0];

    const meta: Record<string, string> | null = state.meta[currentMetaKeyName];
    const type = getType(meta);

    if (type === EVENT_TYPE_REDIRECT || currentState.transitions[0]?.source?.meta?.type === 'end') {
      sendEvent({
        ...flowBaseEvent,
        sendAt: new Date().getTime(),
        target: 'flow',
        stepId: null,
        triggerId: null,
        context: currentState.event?.data || {},
        state: currentState.value as string,
        type: EVENT_TYPE_FLOW_COMPLETE,
      }).then(() => {
        if (type !== EVENT_TYPE_REDIRECT) window.location.reload();
      });
      sendParent({
        state: currentState.value as string,
        type: EVENT_TYPE_FLOW_COMPLETE,
        flowId,
      });
    }
  }, [flowBaseEvent, flowId, isStandaloneApp, sendParent, state]);

  const redirectToExternal = async (
    meta: Record<string, string>,
    currentState: State<
      IContext,
      StateEvent,
      StateSchema<IContext>,
      { value: string; context: IContext }
    >,
  ) => {
    const returnedData = await Promise.allSettled(promises);

    if (returnedData.length <= 0 || returnedData[returnedData.length - 1].status === 'fulfilled') {
      window.location.href = addHttps(
        interpolateMustachifiedHTML(meta?.redirectUrl, currentState.context),
      );
    } else {
      setErrorMessage('Oh no! Something went wrong. Please try again');
    }
  };

  useEffect(() => {
    if (!isStandaloneApp) return;

    const currentState = stateRef.current;
    const currentMetaKeyName = Object.keys(currentState.meta)[0];

    const meta: Record<string, string> | null = state.meta[currentMetaKeyName];
    const type = getType(meta);

    if (type === EVENT_TYPE_REDIRECT && meta?.redirectUrl) {
      const eventData: CreateEventType = {
        ...flowBaseEvent,
        sendAt: new Date().getTime(),
        target: 'step',
        stepId: null,
        triggerId: null,
        type: EVENT_TYPE_REDIRECT,
        meta: null,
        context: currentState.context,
        state: currentState.value as string,
      };
      sendEvent(eventData);
      redirectToExternal(meta, currentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStandaloneApp, state.meta]);

  useEffect(() => {
    const currentState = stateRef.current;
    const currentMetaKeyName = Object.keys(currentState.meta)[0];

    const meta: Record<string, string> | null = state.meta[currentMetaKeyName];
    const type = getType(meta);

    if ((currentState.event.type as string) === 'xstate.init') {
      return;
    }

    if (type === EVENT_TYPE_STATE_ENTER && meta?.type !== 'external') {
      const eventData: CreateEventType = {
        ...flowBaseEvent,
        sendAt: new Date().getTime(),
        target: 'step',
        stepId: meta?.stepId ?? '',
        triggerId: (currentState.context?.triggerId as string) ?? null,
        type: EVENT_TYPE_STATE_ENTER,
        meta: null,
        context: currentState.context,
        state: currentState.value as string,
      };
      sendEvent(eventData);
    }
  }, [flowBaseEvent, state.meta]);

  useEffect(() => {
    const currentState = stateRef.current;
    const currentMetaKeyName = Object.keys(currentState.meta)[0];

    const meta: Record<string, string> | null = state.meta[currentMetaKeyName];
    const type = getType(meta);

    if (currentState.value === ORIGIN_STATE_ID) {
      return;
    }

    const interpolatedRedirectUrl = meta?.redirectUrl
      ? addHttps(interpolateMustachifiedHTML(meta.redirectUrl, currentState.context))
      : '';

    sendParent({
      state: `${state.value}`,
      type,
      redirectUrl: interpolatedRedirectUrl,
      flowId,
      ...(state.meta[currentMetaKeyName]?.type && {
        nextStep: {
          type: state.meta[currentMetaKeyName]?.type,
        },
      }),
      data: {},
    });
  }, [flowId, sendParent, state.meta, state.value]);

  logger.setContext('StateMachineProvider', {
    state,
    busy,
    errors,
  });

  logger.setContext('StateMachineProvider', {
    state,
    busy,
    errors,
  });

  return (
    <StateMachineContext.Provider
      value={{
        state,
        hasStepOnBackBtn,
        busy,
        errorMessage,
        restoreSubmits,
      }}
    >
      {validFlow ? (
        children
      ) : (
        <span>
          We have detected that your flow doesn&apos;t have any step. Please Add a step to your flow
          and republish it.
        </span>
      )}
    </StateMachineContext.Provider>
  );
}
